import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { coursesData } from "../data/courses/courseData.ts";
import { updateSelectedLanguage } from "../utils/userStorage.ts";
import DotsBackground from "../components/DotsBackground.tsx";
import { Instagram, Linkedin } from "lucide-react";

const RegisterPage = () => {
  const navigate = useNavigate();
  const [loadingIdx, setLoadingIdx] = useState<number | null>(null);

  // Debug log when component loads
  console.log("📝 RegisterPage loaded - user needs to select language"); // Debug log

  const availableCourses = coursesData.filter((c) => c.active);
  const comingSoonCourses = coursesData.filter((c) => !c.active);

  // Responsive breakpoints
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1023 });
  const isDesktop = useMediaQuery({ minWidth: 1024 });

  const handleSelect = (idx: number) => {
    const selectedCourse = availableCourses[idx];
    console.log("🎯 Course selected:", selectedCourse.label); // Debug log

    if (selectedCourse.label === "Batak Toba") {
      setLoadingIdx(idx);
      console.log("⏳ Starting language selection process..."); // Debug log

      setTimeout(() => {
        // Save the selected language to user data
        const success = updateSelectedLanguage(selectedCourse.label);
        console.log("💾 Language save result:", success); // Debug log

        if (success) {
          // Mark course as active (for demo purposes)
          availableCourses[idx].active = true;
          console.log("🏠 Navigating to HomePage..."); // Debug log
          navigate("/learn");
        } else {
          console.error("❌ Failed to save language selection");
          setLoadingIdx(null);
        }
      }, 1500);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col relative">
      <DotsBackground />
      {/* Header */}
      <header className="flex items-center px-8 py-6 relative z-10">
        <div className="flex items-center gap-2">
          <img
            src="../assets/aksa-icon-square.png"
            alt="Aksa Icon"
            className="w-8 h-8"
          />
          <span
            className="text-2xl font-bold tracking-tight"
            style={{ fontFamily: "Feather Bold" }}
          >
            <span style={{ color: "#1A365D" }}>Galaxy</span>{" "}
            <span style={{ color: "#EF4444" }}>Aksara</span>
          </span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center relative z-10 px-4">
        <h1
          className="text-2xl md:text-3xl font-bold text-gray-800 mb-8 text-center"
          style={{ fontFamily: "Feather Bold" }}
        >
          Saya ingin belajar bahasa...
        </h1>
        {loadingIdx === null ? (
          <>
            {/* Tersedia */}
            <div className="w-full max-w-4xl mb-2">
              <div className="flex items-center mb-2">
                <div className="flex-1 border-t border-gray-300"></div>
                <span
                  className="mx-4 text-lg font-bold text-gray-800 mb-2"
                  style={{ fontFamily: "DIN Next Rounded" }}
                >
                  Tersedia
                </span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>
              <div className={`flex flex-wrap justify-center ${isMobile || isTablet ? "gap-2 mb-4" : "gap-6 mb-8"}`}>
                {availableCourses.map((course, idx) => (
                  <button
                    key={course.label}
                    className={`flex flex-col items-center justify-center bg-white rounded-2xl border-2 transition ${isMobile || isTablet ? "p-2 w-32 h-24" : "p-6 w-48 h-36"} shadow group focus:outline-none border-gray-400 hover:border-blue-400`}
                    onClick={() => handleSelect(idx)}
                    type="button"
                    disabled={loadingIdx !== null}
                  >
                    <span
                      className={`${isMobile || isTablet ? "text-base mb-0.5" : "text-md mb-1"} font-semibold text-gray-800 text-center`}
                      style={{ fontFamily: "Feather Bold" }}
                    >
                      {loadingIdx === idx ? (
                        <span className={isMobile || isTablet ? "text-2xl font-bold text-gray-500 animate-pulse" : "text-3xl font-bold text-gray-500 animate-pulse"}>
                          ...
                        </span>
                      ) : (
                        course.label
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Segera Hadir */}
            <div className="w-full max-w-4xl">
              <div className="flex items-center mb-2">
                <div className="flex-1 border-t border-gray-300"></div>
                <span
                  className="mx-4 text-lg font-bold text-gray-700 mb-2"
                  style={{ fontFamily: "DIN Next Rounded" }}
                >
                  Segera Hadir!
                </span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>
              <div className={`flex flex-wrap justify-center ${isMobile || isTablet ? "gap-2 mb-4" : "gap-6 mb-8"}`}>
                {comingSoonCourses.map((course) => (
                  <button
                    key={course.label}
                    className={`flex flex-col items-center justify-center bg-white rounded-2xl border-2 transition ${isMobile || isTablet ? "p-2 w-32 h-24" : "p-6 w-48 h-36"} shadow group focus:outline-none border-gray-200 opacity-80 cursor-not-allowed`}
                    type="button"
                    disabled
                    tabIndex={-1}
                    aria-disabled="true"
                    style={{
                      pointerEvents: "none",
                    }}
                  >
                    <span
                      className={`${isMobile || isTablet ? "text-base mb-0.5" : "text-md mb-1"} font-semibold text-gray-300 text-center`}
                      style={{ fontFamily: "Feather Bold" }}
                    >
                      {course.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center w-full h-96">
            <span
              className="text-7xl font-bold text-gray-500 animate-pulse"
              style={{ fontFamily: "Feather Bold" }}
            >
              ...
            </span>
          </div>
        )}
      </main>
      <footer className="w-full py-6 mt-8 bg-transparent flex flex-col items-center gap-2 z-10 relative">
        <div
          className="text-gray-500 text-sm font-semibold"
          style={{ fontFamily: "DIN Next Rounded" }}
        >
          &copy; 2025 Galaxy Developer
        </div>
        <div className="flex gap-4 mt-1">
          <a
            href="https://www.instagram.com/galaxy.developer/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-gray-500 hover:text-pink-500 transition font-semibold text-sm"
          >
            <Instagram className="text-lg" />
          </a>
          <a
            href="https://www.linkedin.com/company/galaxydeveloper"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-gray-500 hover:text-blue-700 transition font-semibold text-sm"
          >
            <Linkedin className="text-lg" />
          </a>
        </div>
      </footer>
    </div>
  );
};

export default RegisterPage;
